import { useEffect, useState } from 'react'
import { listDevices, deleteDevice, type NotificationDevice } from '../../api/notifications'
import { getAccountSummary } from '../../api/account'
import { getToken } from '../../lib/auth'
import { useMe } from '../../hooks/useMe'
import SellerOnboardingPanel from '../../components/settings/SellerOnboardingPanel'

export default function IntegrationsSettings() {
  const authed = Boolean(getToken())
  const { me, refresh } = useMe(authed)
  const [devices, setDevices] = useState<NotificationDevice[] | null>(null)
  const [account, setAccount] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  async function load() {
    if (!authed) return
    setLoading(true)
    setError(null)
    try {
      const [d, a] = await Promise.all([listDevices(), getAccountSummary()])
      setDevices(d)
      setAccount(a)
    } catch (e: any) {
      setError(e?.message || 'Failed to load integrations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [authed])

  async function onRemoveDevice(id: number) {
    setMsg(null)
    try {
      await deleteDevice(id)
      setDevices((ds) => (ds ? ds.filter((d) => d.id !== id) : ds))
      setMsg('Device removed')
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Remove failed')
    }
  }

  const connected = account?.wallet?.connected_account

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-extrabold">Integrations</h1>
      {loading && <div className="rounded-xl border border-white/10 p-4">Loading…</div>}
      {error && <div className="rounded-xl border border-white/10 p-4 text-red-400">{error}</div>}

      <SellerOnboardingPanel me={me} connectedAccount={connected} onRefresh={refresh} />

      <div className="rounded-xl border border-white/10 p-4">
        <div className="font-semibold mb-2">Notification devices</div>
        {devices && devices.length > 0 ? (
          <ul className="text-sm text-white/85 divide-y divide-white/10">
            {devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between py-2">
                <div>
                  <div>{d.platform || 'device'} {d.app_version ? `v${d.app_version}` : ''}</div>
                  <div className="text-xs text-white/60">Last seen: {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : '—'}</div>
                </div>
                <button onClick={() => onRemoveDevice(d.id)} className="px-3 py-1.5 rounded-lg border border-white/15 hover:bg-white/5 text-sm">Remove</button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-white/70">No registered devices.</div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 p-4">
        <div className="font-semibold mb-2">Stripe Connect</div>
        {connected ? (
          <div className="text-sm text-white/85 space-y-1">
            <div>Account: {connected.account_id}</div>
            <div>Charges: {connected.charges_enabled ? 'Enabled' : 'Disabled'} | Payouts: {connected.payouts_enabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        ) : (
          <div className="text-sm text-white/70">Not connected.</div>
        )}
      </div>

      {msg && <div className="rounded-lg border border-white/10 p-2 text-sm text-white/85">{msg}</div>}
    </div>
  )
}
